/**
 * The reusable overlay unit: Range A as a filled area (solid) + Range B as a dashed line,
 * same hue, with a legend labeled by date range and a custom tooltip. Used both as the
 * single big chart and (compact) in the small-multiples grid.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useMemo } from 'react';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import type { AlignedRow } from '../../data/types';
import type { CompareMetric } from '../../metrics/compareMetrics';
import { formatAxisTick } from '../../metrics/metricFormat';
import { metricStroke } from '../../metrics/metricColor';
import { buildChartRows } from './compareChartSeries';
import { CompareChartTooltip } from './CompareChartTooltip';

interface Props {
  metric: CompareMetric;
  aligned: AlignedRow[];
  currency: string;
  labelA: string;
  labelB: string;
  compact?: boolean;
}

export function CompareMetricChart({ metric, aligned, currency, labelA, labelB, compact }: Props) {
  const rows = useMemo(() => buildChartRows(aligned, metric), [aligned, metric]);
  const stroke = metricStroke(metric);
  const gradientId = `cmp-${metric.key.replace(/\./g, '-')}`;

  return (
    <div className="rounded-lg border bg-card p-3">
      <div
        className={
          compact
            ? 'mb-1 truncate text-xs font-semibold text-foreground'
            : 'mb-2 text-sm font-medium text-foreground'
        }
      >
        {metric.label}
      </div>
      <div className={compact ? 'h-44 w-full' : 'h-[300px] w-full'}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={rows} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={stroke} stopOpacity={0.28} />
                <stop offset="100%" stopColor={stroke} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" className="stroke-border" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" minTickGap={16} />
            <YAxis
              tick={{ fontSize: 10 }}
              width={48}
              tickFormatter={(v: number) => formatAxisTick(metric, v, currency)}
            />
            <Tooltip
              content={
                <CompareChartTooltip metric={metric} currency={currency} labelA={labelA} labelB={labelB} />
              }
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Area
              type="monotone"
              dataKey="a"
              name={labelA}
              stroke={stroke}
              strokeWidth={2}
              fill={`url(#${gradientId})`}
              connectNulls
              dot={false}
              animationDuration={600}
            />
            <Line
              type="monotone"
              dataKey="b"
              name={labelB}
              stroke={stroke}
              strokeOpacity={0.55}
              strokeWidth={2}
              strokeDasharray="4 4"
              connectNulls
              dot={false}
              animationDuration={600}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default CompareMetricChart;
